/**
 * OpenFreeMap merges unrelated buildings with identical attributes into one
 * MVT MultiPolygon. In the 2026-09-13 z14 tile the clock tower is polygon 26
 * of 84, in feature 70080790 (render_height=5). Those indices/IDs are NOT
 * stable identifiers, so select the small component by its measured footprint.
 *
 * Remove that whole component before MapLibre creates extrusion geometry.
 * Preserve holes, IDs, properties, other features and other layers. No mask,
 * feature-state trick, or frozen GeoJSON replacement of the surrounding block.
 */
import { VectorTileLayer, classifyRings } from '@mapbox/vector-tile';
import { PbfReader, PbfWriter } from 'pbf';

// Measured vector footprint, slightly south of the GLB's placement point.
const CENTER = [10.728283, 59.909813];
const HALF_SIZE_METERS = 3;
const EARTH_CIRCUMFERENCE = 40075016.68557849;
const mercatorX = (CENTER[0] + 180) / 360;
const mercatorY = (1 - Math.asinh(Math.tan(CENTER[1] * Math.PI / 180)) / Math.PI) / 2;
const metersPerWorld = EARTH_CIRCUMFERENCE * Math.cos(CENTER[1] * Math.PI / 180);

export function tileMayContainClocktower({ z, x, y }) {
  // The tower does not exist in the generalized z13 tiles. Higher map zooms
  // normally overzoom the same z14 source tile, but also support future tiles.
  if (z < 14) return false;
  const n = 2 ** z;
  const margin = HALF_SIZE_METERS * n / metersPerWorld + 0.0625;
  // Include tile buffers, including a footprint duplicated across tile edges.
  return mercatorX * n >= x - margin && mercatorX * n <= x + 1 + margin &&
    mercatorY * n >= y - margin && mercatorY * n <= y + 1 + margin;
}

function isClocktowerPolygon(rings, extent, { z, x, y }) {
  const unitsPerMeter = extent * 2 ** z / metersPerWorld;
  const cx = (mercatorX * 2 ** z - x) * extent;
  const cy = (mercatorY * 2 ** z - y) * extent;
  const limit = HALF_SIZE_METERS * unitsPerMeter;
  const outer = rings[0];
  if (!outer || outer.length < 4) return false;
  // A large connected polygon that passes through the location is not removed.
  if (!outer.every(p => Math.abs(p.x - cx) <= limit && Math.abs(p.y - cy) <= limit)) return false;
  let twiceArea = 0;
  for (let i = 1; i < outer.length; i++) {
    twiceArea += outer[i - 1].x * outer[i].y - outer[i].x * outer[i - 1].y;
  }
  const areaM2 = Math.abs(twiceArea) / (2 * unitsPerMeter ** 2);
  return areaM2 >= 1 && areaM2 <= 20;
}

function concat(chunks) {
  const result = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
  let offset = 0;
  for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
  return result;
}

/** Rewrite only selected length-delimited fields; copy all other bytes exactly.
 * Returning undefined retains a field, null removes it, Uint8Array replaces it.
 */
function rewriteMessages(bytes, fieldNumber, transform) {
  const reader = new PbfReader(bytes);
  const chunks = [];
  let ordinal = 0;
  let changed = false;
  while (reader.pos < reader.length) {
    const start = reader.pos;
    const tag = reader.readVarint();
    let replacement;
    if (tag === (fieldNumber * 8 + 2)) {
      replacement = transform(reader.readBytes(), ordinal++);
    } else {
      reader.skip(tag);
    }
    if (replacement === undefined) {
      chunks.push(bytes.subarray(start, reader.pos));
    } else {
      changed = true;
      if (replacement !== null) {
        const writer = new PbfWriter();
        writer.writeBytesField(fieldNumber, replacement);
        chunks.push(writer.finish());
      }
    }
  }
  return changed ? concat(chunks) : undefined;
}

function encodeRings(polygons) {
  const writer = new PbfWriter();
  let x = 0, y = 0;
  for (const ring of polygons.flat()) {
    const last = ring[ring.length - 1];
    const closed = last.x === ring[0].x && last.y === ring[0].y;
    const points = closed ? ring.slice(0, -1) : ring;
    writer.writeVarint(9); // one MoveTo
    writer.writeSVarint(points[0].x - x);
    writer.writeSVarint(points[0].y - y);
    x = points[0].x; y = points[0].y;
    writer.writeVarint((points.length - 1) * 8 + 2); // LineTo
    for (let i = 1; i < points.length; i++) {
      writer.writeSVarint(points[i].x - x);
      writer.writeSVarint(points[i].y - y);
      x = points[i].x; y = points[i].y;
    }
    writer.writeVarint(15); // one ClosePath; cursor stays at last LineTo
  }
  return writer.finish();
}

/** @returns {{ data: ArrayBuffer, removedPolygons: number }} */
export function removeClocktowerFootprint(buffer, tile) {
  if (!tileMayContainClocktower(tile)) return { data: buffer, removedPolygons: 0 };
  const bytes = new Uint8Array(buffer);
  let removedPolygons = 0;
  const rewritten = rewriteMessages(bytes, 3, layerBytes => {
    const layer = new VectorTileLayer(new PbfReader(layerBytes));
    if (layer.name !== 'building') return undefined;
    return rewriteMessages(layerBytes, 2, (featureBytes, index) => {
      const feature = layer.feature(index);
      if (feature.type !== 3 || feature.properties.render_height !== 5 ||
          (feature.properties.render_min_height ?? 0) !== 0) return undefined;
      const polygons = classifyRings(feature.loadGeometry());
      const kept = polygons.filter(rings => !isClocktowerPolygon(rings, layer.extent, tile));
      if (kept.length === polygons.length) return undefined;
      removedPolygons += polygons.length - kept.length;
      if (!kept.length) return null;
      // Deltas span rings, so re-encode the remaining geometry as one stream.
      // Feature ID/tags and the layer's shared attribute tables stay untouched.
      return rewriteMessages(featureBytes, 4, () => encodeRings(kept));
    });
  });
  return { data: rewritten ? rewritten.buffer : buffer, removedPolygons };
}
